import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Calendar, GraduationCap, AlertCircle } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    enquiryType: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${backendUrl}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', enquiryType: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 8000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Hero Section with Photo */}
      <div className="relative h-96 bg-black">
        <img
          src="https://customer-assets.emergentagent.com/job_07e03d84-96d6-4787-9cf6-a4abd4f4b8fa/artifacts/ljami5b3_IMG_9385.JPG"
          alt="Contact"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-12 max-w-7xl mx-auto">
          <h1 className="text-5xl font-light text-white mb-4">Get In Touch</h1>
          <p className="text-xl text-gray-300">We'd love to hear from you</p>
        </div>
      </div>

      {/* Contact Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information - Two Sections */}
          <div>
            <h2 className="text-3xl font-light text-gray-900 mb-8">Contact Information</h2>
            
            {/* Booking Enquiries Section */}
            <div className="mb-10 p-6 bg-orange-50 rounded-2xl border border-orange-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                  <Calendar className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Booking Enquiries</h3>
                  <p className="text-sm text-orange-600 font-medium">Speaking & Ministry Invitations</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4 text-sm">
                For speaking engagements, ministry invitations, conferences, events, and all booking-related enquiries.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-orange-500 mr-3" />
                  <a href="mailto:info@obedansuministries.com" className="text-gray-700 hover:text-orange-600 transition-colors">
                    info@obedansuministries.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-orange-500 mr-3" />
                  <a href="tel:+447368203033" className="text-gray-700 hover:text-orange-600 transition-colors">
                    +44 7368 203033
                  </a>
                </div>
              </div>
            </div>

            {/* Mentorship Enquiries Section */}
            <div className="mb-10 p-6 bg-purple-50 rounded-2xl border border-purple-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Mentorship Enquiries</h3>
                  <p className="text-sm text-purple-600 font-medium">Academy & Spiritual Formation</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4 text-sm">
                For mentorship programs, spiritual formation, discipleship, and academy-related questions.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-purple-500 mr-3" />
                  <a href="mailto:mentorship@obedansuministries.com" className="text-gray-700 hover:text-purple-600 transition-colors">
                    mentorship@obedansuministries.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-purple-500 mr-3" />
                  <a href="tel:+447735006222" className="text-gray-700 hover:text-purple-600 transition-colors">
                    +44 7735 006222
                  </a>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start mb-8">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                <p className="text-gray-600">London, United Kingdom</p>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors">
                  <span className="text-gray-700 font-semibold">f</span>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors">
                  <span className="text-gray-700 font-semibold">in</span>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 hover:bg-pink-100 rounded-full flex items-center justify-center transition-colors">
                  <span className="text-gray-700 font-semibold">IG</span>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-light text-gray-900 mb-6">Send Us a Message</h2>
              
              {submitted && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-green-700 font-medium">Thank you for your enquiry!</p>
                    <p className="text-green-600 text-sm mt-1">
                      {formData.enquiryType === 'bookings' 
                        ? "Our bookings team will review your request and follow up with availability and next steps."
                        : "Our mentorship team will get back to you shortly with more information."}
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="+44 7xxx xxxxxx"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="enquiryType" className="block text-sm font-medium text-gray-700 mb-2">
                    Enquiry Type *
                  </label>
                  <select
                    id="enquiryType"
                    name="enquiryType"
                    required
                    value={formData.enquiryType}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white ${
                      formData.enquiryType === '' ? 'text-gray-400' : 'text-gray-900'
                    }`}
                  >
                    <option value="" disabled>Select the nature of your enquiry</option>
                    <option value="mentorship">Mentorship</option>
                    <option value="bookings">Bookings</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1.5">
                    Please select so your enquiry reaches the right team.
                  </p>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={formData.enquiryType === 'bookings' ? "e.g., Speaking engagement for conference" : "e.g., Question about mentorship program"}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder={formData.enquiryType === 'bookings' 
                      ? "Please include event details: date, location, type of engagement, expected audience size, etc."
                      : "Your message..."}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-4 rounded-xl font-medium transition-colors flex items-center justify-center disabled:opacity-50 ${
                    formData.enquiryType === 'bookings'
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send {formData.enquiryType === 'bookings' ? 'Booking Request' : 'Message'}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
